
import { cloneElement, isValidElement } from "react";
import { motion } from "framer-motion";

export default function Card({ title, value, icon, bg }) {
  const iconToneByBackground = {
    "bg-green-100": "text-emerald-600",
    "bg-purple-100": "text-violet-600",
    "bg-red-100": "text-rose-600",
    "bg-blue-100": "text-sky-600",
  };
  const surfaceByBackground = {
    "bg-green-100":
      "from-emerald-50 via-cyan-50 to-white text-emerald-950 shadow-emerald-900/18",
    "bg-purple-100":
      "from-violet-50 via-fuchsia-50 to-white text-violet-950 shadow-violet-900/18",
    "bg-red-100":
      "from-rose-50 via-orange-50 to-white text-rose-950 shadow-rose-900/18",
    "bg-blue-100":
      "from-sky-50 via-blue-50 to-white text-sky-950 shadow-sky-900/18",
  };

  const iconClassName = iconToneByBackground[bg] || "text-slate-700";
  const renderedIcon = isValidElement(icon)
    ? cloneElement(icon, {
        className: [icon.props.className, iconClassName].filter(Boolean).join(" "),
        strokeWidth: icon.props.strokeWidth || 2.5,
      })
    : icon;

  return (
    <motion.div
      whileHover={{
        y: -10,
        scale: 1.05,
        rotateX: 6,
        rotateY: -6,
      }}
      transition={{ type: "spring", stiffness: 180, damping: 12 }}
      className={`
        bg-gradient-to-br ${surfaceByBackground[bg] || "from-white via-slate-50 to-white text-slate-950 shadow-slate-900/18"}
        rounded-[22px]
        p-4
        relative
        overflow-hidden
        backdrop-blur-xl
        border border-white/70
        shadow-[0_22px_60px_var(--tw-shadow-color),inset_0_1px_0_rgba(255,255,255,0.95)]
        hover:shadow-[0_36px_90px_var(--tw-shadow-color),inset_0_1px_0_rgba(255,255,255,0.95)]
        group
        cursor-pointer
        min-h-[118px]
      `}
      style={{ transformStyle: "preserve-3d" }}
    >
      {/* soft inner glow */}
      <div className="
        absolute inset-0
        bg-gradient-to-br
        from-white/40
        via-white/10
        to-transparent
        opacity-60
        pointer-events-none
      "/>

      {/* animated light sweep */}
      <div className="
        absolute -inset-12
        bg-gradient-to-r
        from-transparent
        via-white/55
        to-transparent
        opacity-0
        group-hover:opacity-100
        transition
        duration-700
        rotate-12
      "/>

      <div className="
        absolute -right-7 -top-7
        h-20 w-20 rounded-full
        bg-white/38 blur-xl
        transition duration-500
        group-hover:scale-125
      "/>
      <div className="
        absolute bottom-2 right-3
        h-9 w-16 rounded-full
        bg-black/10 blur-xl
        opacity-45
      "/>

      {/* icon bubble */}
      <div className="
        relative
        w-11 h-11
        rounded-2xl
        bg-white/88
        flex items-center justify-center
        mb-4
        shadow-[0_14px_28px_rgba(15,23,42,0.16),inset_0_1px_0_rgba(255,255,255,0.95)]
        ring-1 ring-white/50
        transition duration-300
        group-hover:-translate-y-1
      ">
        {renderedIcon}
      </div>

      <p className="relative text-sm font-medium text-slate-500">{title}</p>
      <h2 className="relative mt-0.5 text-2xl font-black tracking-tight text-slate-950">
        {value}
      </h2>
    </motion.div>
  );
}

